/// <reference types="@types/googlemaps" />
/*global google*/

const METERS_TO_MILES = 1609.344;

// For group level (property clusters)
const PROPERTY_MAP_ZOOM_LEVEL = 17;
const CENSUS_TRACT_MAP_ZOOM_LEVEL = 15;
const ZIPCODE_MAP_ZOOM_LEVEL = 12;
const CITY_MAP_ZOOM_LEVEL = 11;
const CBSA_MAP_ZOOM_LEVEL = 9;
const STATE_MAP_ZOOM_LEVEL = 6;

export const propertyGroupLevels = [
    {
        value: 'State',
        caption: 'State',
        autoZoom: STATE_MAP_ZOOM_LEVEL,
        minZoom: 4,
        maxZoom: 9
    },
    {
        value: 'CBSA',
        caption: 'CBSA',
        autoZoom: CBSA_MAP_ZOOM_LEVEL,
        minZoom: 4,
        maxZoom: 11
    },
    {
        value: 'City',
        caption: 'City',
        autoZoom: CITY_MAP_ZOOM_LEVEL,
        minZoom: 8,
        maxZoom: 15
    },
    {
        value: 'Zipcode',
        caption: 'Zip Code',
        autoZoom: ZIPCODE_MAP_ZOOM_LEVEL,
        minZoom: 9,
        maxZoom: 18
    },
    {
        value: 'CensusTractName',
        caption: 'Census Tract',
        autoZoom: CENSUS_TRACT_MAP_ZOOM_LEVEL,
        minZoom: 9,
        maxZoom: 22
    },
    {
        value: 'Property',
        caption: 'Property',
        autoZoom: PROPERTY_MAP_ZOOM_LEVEL,
        minZoom: 9,
        maxZoom: 22
    }
];

const shapesGeoTypes = [
    {
        value: 'State',
        caption: 'State',
        autoZoom: 6,
        minZoom: 4,
        maxZoom: 9
    },
    {
        value: 'CBSA',
        caption: 'CBSA',
        autoZoom: 9,
        minZoom: 4,
        maxZoom: 11
    },
    {
        value: 'County',
        caption: 'County',
        autoZoom: 10,
        minZoom: 8,
        maxZoom: 14
    },
    {
        value: 'Zip Code',
        caption: 'Zip Code',
        autoZoom: 12,
        minZoom: 9,
        maxZoom: 18
    },
    {
        value: 'Census Tract',
        caption: 'Census Tract',
        autoZoom: 19,
        minZoom: 9,
        maxZoom: 22
    },
    {
        value: 'Opportunity Zone',
        caption: 'Opportunity Zone',
        autoZoom: 0, // Auto skips opportunity zones
        minZoom: 9,
        maxZoom: 22
    },
    {
        value: 'Neighborhood Level 1',
        caption: 'Major Community',
        autoZoom: 0, // Auto uses Neighborhood 4 only, skip 1-3
        minZoom: 9,
        maxZoom: 16
    },
    {
        value: 'Neighborhood Level 2',
        caption: 'Neighborhood',
        autoZoom: 0,
        minZoom: 11,
        maxZoom: 18
    },
    {
        value: 'Neighborhood Level 3',
        caption: 'Sub Neighborhood',
        autoZoom: 0,
        minZoom: 13,
        maxZoom: 20
    },
    {
        value: 'Neighborhood Level 4',
        caption: 'Micro Neighborhood',
        autoZoom: 22,
        minZoom: 15,
        maxZoom: 22
    }
];

export function getShapeGeoTypes() {
  return shapesGeoTypes;
}

// For shapes geo types (auto shapes)
export function convertZoomToShapesAutoGeoType(zoom) {
    let foundType = shapesGeoTypes.find(type => zoom <= type.autoZoom)?.value;
    return foundType || 'State';
}

// For shapes geo types (manual shape type selection)
export function getManualShapesGeoTypesForZoomLevel(zoom) {
    return shapesGeoTypes.map(type => {
        return {
            ...type,
            enabled: zoom >= type.minZoom && zoom <= type.maxZoom
        }
    });
}

// For property data geo types (auto)
export function convertZoomToPropertyGroupLevel(zoom) {
    let foundType = propertyGroupLevels.find(type => zoom <= type.autoZoom)?.value;
    return foundType || "Property";
}

// For property data geo types (manual type selection)
export function getManualPropertyGroupLevelForZoomLevel(zoom) {
    return propertyGroupLevels.map(type => {
        return {
            ...type,
            enabled: zoom >= type.minZoom && zoom <= type.maxZoom
        }
    });
}

function getCornerFromBounds(bounds) {
    return { lat: bounds.north, lng: bounds.east };
}

export function getRadiusInMiles(center, bounds) {
    const corner = getCornerFromBounds(bounds);
    const centerLatLng = new google.maps.LatLng(center.lat, center.lng);
    const cornerLatLng = new google.maps.LatLng(corner.lat, corner.lng);
    const radiusInMeters = google.maps.geometry.spherical.computeDistanceBetween(centerLatLng, cornerLatLng)
    return metersToMiles(radiusInMeters);
}

function metersToMiles(meters) {
    return meters / METERS_TO_MILES;
}

export function parseGooglePlace(googlePlace) {
    try {
        if (googlePlace && googlePlace.types && googlePlace.types.length > 0) {
            var cluster = {};
            var stateType = googlePlace.types.find(t => t === "administrative_area_level_1");
            var cityType = googlePlace.types.find(t => t === "locality");
            var zipcodeType = googlePlace.types.find(t => t === "postal_code");
            var addressType = googlePlace.types.find(t => t === "premise" || t === "street_address");

            if (googlePlace.address_components) {
                var stateComponent = googlePlace.address_components.find(ac => ac.types && ac.types.indexOf("administrative_area_level_1") >= 0);
                if (stateComponent) {
                    cluster.state = stateComponent.short_name;
                }
                var cityComponent = googlePlace.address_components.find(ac => ac.types && ac.types.indexOf("locality") >= 0);
                if (cityComponent) {
                    cluster.city = cityComponent.short_name;
                    cluster.city = cluster.city.toUpperCase();
                }
                var zipcodeComponent = googlePlace.address_components.find(ac => ac.types && ac.types.indexOf("postal_code") >= 0);
                if (zipcodeComponent) {
                    cluster.zipcode = zipcodeComponent.short_name;
                }
                var streetNumberComponent = googlePlace.address_components.find(ac => ac.types && ac.types.indexOf("street_number") >= 0);
                if (streetNumberComponent) {
                    cluster.streetNumber = streetNumberComponent.short_name;
                }
                var streetNameComponent = googlePlace.address_components.find(ac => ac.types && ac.types.indexOf("route") >= 0);
                if (streetNameComponent) {
                    cluster.streetName = streetNameComponent.short_name;
                }
            }

            if (addressType) {
                cluster.groupLevel = "Property";
                cluster.zoom = PROPERTY_MAP_ZOOM_LEVEL;
                cluster.id = null; // cluster.StreetNumber + " " + cluster.StreetName.toUpperCase();
            } else if (zipcodeType) {
                cluster.groupLevel = "Zipcode";
                cluster.zoom = ZIPCODE_MAP_ZOOM_LEVEL;
                cluster.id = cluster.zipcode;
            } else if (cityType) {
                cluster.groupLevel = "City";
                cluster.zoom = CITY_MAP_ZOOM_LEVEL;                
                cluster.id = cluster.city + ", " + cluster.state;            
            } else if (stateType) {
                cluster.groupLevel = "State";
                cluster.zoom = STATE_MAP_ZOOM_LEVEL;
                cluster.id = cluster.state;
            } else {
                cluster.groupLevel = null;
                cluster.zoom = null;
                cluster.id = null;
            }

     



            return cluster;
        }        
    } catch (error) {
        console.log(error);
    }
    return null;
}